import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cloud,
  Eye,
  EyeOff,
  Send,
  Loader2,
  Plus,
  X,
  Lock,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShareLink } from "@/components/ShareLink";
import { useNoteUpload } from "@/hooks/useNoteUpload";
import { useServerConfig } from "@/hooks/useServerConfig";
import { formatBytes, formatDuration } from "@/lib/utils";

type ObjectStorageSubTab = "share";

type CapacityUnit = "GB" | "TB" | "PB";

type BucketEntry = {
  name: string;
  objectLock: boolean;
};

type ExpirationDateFields = {
  date: string;
  time: string;
  period: "AM" | "PM";
};

export function ObjectStorageForm({ forcePassword = false }: { forcePassword?: boolean }) {
  const { t } = useTranslation();
  const { config } = useServerConfig();
  const noteHook = useNoteUpload();

  const [objectStorageSubTab] = useState<ObjectStorageSubTab>("share");

  const [tenantAccountName, setTenantAccountName] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [showSecretAccessKey, setShowSecretAccessKey] = useState(false);
  const [region, setRegion] = useState("");

  const [capacityValue, setCapacityValue] = useState("");
  const [capacityUnit, setCapacityUnit] = useState<CapacityUnit>("TB");

  const [expirationDate, setExpirationDate] = useState<ExpirationDateFields>({
    date: "",
    time: "",
    period: "AM",
  });

  const [buckets, setBuckets] = useState<BucketEntry[]>([
    { name: "", objectLock: false },
  ]);

  const [expireSec, setExpireSec] = useState<number | null>(null);
  const [maxViews, setMaxViews] = useState<number | null>(null);
  const [notePassword, setNotePassword] = useState("");
  const [showNotePassword, setShowNotePassword] = useState(false);
  const [notePasswordEnabled, setNotePasswordEnabled] = useState(forcePassword);

  if (!config) return null;

  const effectiveExpireSec = expireSec ?? config.noteDefaultExpire;
  const effectiveMaxViews = maxViews ?? config.noteDefaultViews;

  const payload = {
    kind: "object-storage-auth",
    tenantAccountName,
    endpointUrl,
    accessKeyId,
    secretAccessKey,
    region,
    capacityLimit:
      capacityValue.trim().length > 0
        ? {
            value: capacityValue,
            unit: capacityUnit,
          }
        : null,
    expirationDate:
      Object.values(expirationDate).some((value) => value.trim().length > 0)
        ? expirationDate
        : null,
    buckets: buckets
      .filter((bucket) => bucket.name.trim().length > 0 || bucket.objectLock)
      .map((bucket) => ({
        name: bucket.name,
        objectLock: bucket.objectLock,
      })),
  };

  const content = JSON.stringify(payload);
  const contentBytes = new TextEncoder().encode(content).length;
  const sizeExceeded = contentBytes > config.noteMaxSize;

  const isSubmitting =
    noteHook.phase === "encrypting" || noteHook.phase === "uploading";

  const canSubmit =
    endpointUrl.trim().length > 0 &&
    accessKeyId.trim().length > 0 &&
    secretAccessKey.trim().length > 0 &&
    !sizeExceeded &&
    !isSubmitting;

  const updateBucketName = (index: number, name: string) => {
    setBuckets((prev) =>
      prev.map((bucket, i) => (i === index ? { ...bucket, name } : bucket)),
    );
  };

  const updateBucketObjectLock = (index: number, objectLock: boolean) => {
    setBuckets((prev) =>
      prev.map((bucket, i) =>
        i === index ? { ...bucket, objectLock } : bucket,
      ),
    );
  };

  const addBucket = () => {
    setBuckets((prev) => [...prev, { name: "", objectLock: false }]);
  };

  const removeBucket = (index: number) => {
    if (buckets.length <= 1) return;
    setBuckets((prev) => prev.filter((_, i) => i !== index));
  };

  const updateExpirationDate = (
    field: keyof ExpirationDateFields,
    value: string,
  ) => {
    setExpirationDate((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    noteHook.upload({
      content,
      contentType: "password",
      maxViews: effectiveMaxViews,
      expireSec: effectiveExpireSec,
      password: notePasswordEnabled ? notePassword : "",
    });
  };

  const handleNewNote = () => {
    noteHook.reset();
    setTenantAccountName("");
    setEndpointUrl("");
    setAccessKeyId("");
    setSecretAccessKey("");
    setShowSecretAccessKey(false);
    setRegion("");
    setCapacityValue("");
    setCapacityUnit("TB");
    setExpirationDate({
      date: "",
      time: "",
      period: "AM",
    });
    setBuckets([{ name: "", objectLock: false }]);
    setNotePassword("");
    setNotePasswordEnabled(forcePassword);
  };

  if (noteHook.phase === "done" && noteHook.shareLink) {
    return <ShareLink link={noteHook.shareLink} onNewUpload={handleNewNote} />;
  }

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="grid grid-cols-1 rounded-lg border bg-muted p-1">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-background px-3 py-2 text-sm font-medium text-foreground shadow-sm"
          >
            <Cloud className="h-4 w-4" />
            {t("objectStorage.shareAuthTab")}
          </button>
        </div>

        {objectStorageSubTab === "share" && (
          <>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("objectStorage.credentials")}</Label>
                <span
                  className={`text-xs ${
                    sizeExceeded
                      ? "text-destructive-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {formatBytes(contentBytes)} / {formatBytes(config.noteMaxSize)}
                </span>
              </div>

              <Input
                type="text"
                value={tenantAccountName}
                onChange={(e) => setTenantAccountName(e.target.value)}
                placeholder={t("objectStorage.tenantAccountNameInput")}
                className="font-mono"
                disabled={isSubmitting}
                autoComplete="off"
              />

              <Input
                type="url"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                placeholder={t("objectStorage.endpointUrlInput")}
                className="font-mono"
                disabled={isSubmitting}
                autoComplete="off"
              />

              <Input
                type="text"
                value={accessKeyId}
                onChange={(e) => setAccessKeyId(e.target.value)}
                placeholder={t("objectStorage.accessKeyIdInput")}
                className="font-mono"
                disabled={isSubmitting}
                autoComplete="off"
              />

              <div className="relative">
                <Input
                  type={showSecretAccessKey ? "text" : "password"}
                  value={secretAccessKey}
                  onChange={(e) => setSecretAccessKey(e.target.value)}
                  placeholder={t("objectStorage.secretAccessKeyInput")}
                  className="pr-10 font-mono"
                  disabled={isSubmitting}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowSecretAccessKey(!showSecretAccessKey)}
                  disabled={isSubmitting}
                >
                  {showSecretAccessKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>

              <Input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder={t("objectStorage.regionInput")}
                className="font-mono"
                disabled={isSubmitting}
                autoComplete="off"
              />

              <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
                <Input
                  type="number"
                  min="0"
                  value={capacityValue}
                  onChange={(e) => setCapacityValue(e.target.value)}
                  placeholder={t("objectStorage.capacityLimitInput")}
                  className="font-mono"
                  disabled={isSubmitting}
                  autoComplete="off"
                />

                <Select
                  value={capacityUnit}
                  onValueChange={(value) => setCapacityUnit(value as CapacityUnit)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GB">GB</SelectItem>
                    <SelectItem value="TB">TB</SelectItem>
                    <SelectItem value="PB">PB</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t("objectStorage.expirationDateInput")}</Label>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_110px]">
                  <Input
                    type="text"
                    value={expirationDate.date}
                    onChange={(e) => updateExpirationDate("date", e.target.value)}
                    placeholder={t("objectStorage.datePlaceholder")}
                    className="font-mono"
                    disabled={isSubmitting}
                    autoComplete="off"
                    inputMode="numeric"
                  />
                  <Input
                    type="text"
                    value={expirationDate.time}
                    onChange={(e) => updateExpirationDate("time", e.target.value)}
                    placeholder={t("objectStorage.timePlaceholder")}
                    className="font-mono"
                    disabled={isSubmitting}
                    autoComplete="off"
                    inputMode="numeric"
                  />
                  <Select
                    value={expirationDate.period}
                    onValueChange={(value) =>
                      updateExpirationDate("period", value as "AM" | "PM")
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
                <div className="flex items-center justify-between">
                  <Label>{t("objectStorage.buckets")}</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addBucket}
                    disabled={isSubmitting}
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    {t("objectStorage.addBucket")}
                  </Button>
                </div>

                {buckets.map((bucket, index) => (
                  <div key={index} className="space-y-2 rounded-md border bg-background/50 p-3">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={bucket.name}
                        onChange={(e) => updateBucketName(index, e.target.value)}
                        placeholder={t("objectStorage.bucketNameInput", {
                          number: index + 1,
                        })}
                        className="font-mono"
                        disabled={isSubmitting}
                        autoComplete="off"
                      />

                      {buckets.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeBucket(index)}
                          disabled={isSubmitting}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <Label
                        htmlFor={`bucket-object-lock-${index}`}
                        className="text-sm text-muted-foreground"
                      >
                        {t("objectStorage.bucketObjectLock")}
                      </Label>
                      <Switch
                        id={`bucket-object-lock-${index}`}
                        checked={bucket.objectLock}
                        onCheckedChange={(checked) =>
                          updateBucketObjectLock(index, checked)
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("objectStorage.expiresAfter")}</Label>
                <Select
                  value={String(effectiveExpireSec)}
                  onValueChange={(value) => setExpireSec(Number(value))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.noteExpireOptions.map((seconds) => (
                      <SelectItem key={seconds} value={String(seconds)}>
                        {formatDuration(seconds)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t("objectStorage.maxViews")}</Label>
                <Select
                  value={String(effectiveMaxViews)}
                  onValueChange={(value) => setMaxViews(Number(value))}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {config.noteViewOptions.map((views) => (
                      <SelectItem key={views} value={String(views)}>
                        {views === 0 ? t("note.unlimited") : views}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t("upload.password")}
                  {forcePassword && (
                    <span className="text-xs text-muted-foreground">
                      ({t("upload.passwordRequired")})
                    </span>
                  )}
                </Label>

                {!forcePassword && (
                  <Switch
                    checked={notePasswordEnabled}
                    onCheckedChange={setNotePasswordEnabled}
                    disabled={isSubmitting}
                  />
                )}
              </div>

              {notePasswordEnabled && (
                <div className="relative">
                  <Input
                    type={showNotePassword ? "text" : "password"}
                    value={notePassword}
                    onChange={(e) => setNotePassword(e.target.value)}
                    placeholder={t("upload.passwordPlaceholder")}
                    autoComplete="off"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowNotePassword(!showNotePassword)}
                  >
                    {showNotePassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {sizeExceeded && (
              <p className="text-sm text-destructive-foreground" role="alert">
                {t("note.tooLarge", {
                  size: formatBytes(config.noteMaxSize),
                  defaultValue: "Conteúdo maior que o limite permitido.",
                })}
              </p>
            )}

            {noteHook.phase === "error" && noteHook.error && (
              <p className="text-sm text-destructive-foreground" role="alert">
                {noteHook.error}
              </p>
            )}

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Send className="mr-2 h-5 w-5" />
              )}
              {isSubmitting ? t("note.creating") : t("note.create")}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
